import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import api from '../../lib/api';
import {
  AlertTriangle, Pill, Activity, Loader2, ChevronDown, ChevronUp, Stethoscope, CalendarDays,
} from 'lucide-react';

/**
 * Resumen clínico del paciente: lo que el médico necesita ver antes de atender.
 *
 * Un solo componente para dos sitios, en dos tamaños:
 *   compact  -> dentro del diálogo de nueva consulta. Alertas + 5 consultas.
 *   full     -> la ficha del paciente. Todo, con más historial.
 *
 * Se comparte a propósito: son la misma información y separarlas garantizaba
 * que una de las dos se quedase atrás.
 *
 * @param {string} patientId
 * @param {'compact'|'full'} [variant='compact']
 * @param {Function} [onOpenConsultation] - Al pulsar una consulta del historial
 */
export default function PatientSummary({ patientId, variant = 'compact', onOpenConsultation }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(variant === 'full');

  const load = useCallback(async () => {
    if (!patientId) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: summary } = await api.get(`/patients/${patientId}/summary`);
      setData(summary);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cargar el resumen del paciente');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setShowAll(variant === 'full'); }, [patientId, variant]);

  if (!patientId) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando historial...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-slate-500 mb-2">{error}</p>
        <Button size="sm" variant="outline" onClick={load}>Reintentar</Button>
      </div>
    );
  }

  if (!data) return null;

  const { paciente, alertas, estadisticas, consultas_recientes: consultas,
          diagnosticos_frecuentes: diagnosticos, medicacion_reciente: medicacion } = data;

  // En el diálogo se muestran 5: más empuja el formulario fuera de la vista.
  const VISIBLE = 5;
  const visibleConsultas = showAll ? consultas : consultas.slice(0, VISIBLE);
  const hiddenCount = consultas.length - visibleConsultas.length;

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date) ? '' : date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Las alergias se pintan en rojo; el resto en ámbar. Una alergia es lo único
  // de esta lista que puede convertir una receta correcta en un daño.
  const isCritical = (tipo) => tipo === 'alergia';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-semibold text-slate-800">{paciente.nombre}</span>
        {paciente.dni && <span className="text-slate-500">DNI {paciente.dni}</span>}
        {paciente.edad !== null && <span className="text-slate-500">{paciente.edad} años</span>}
        {paciente.genero && <span className="text-slate-500 capitalize">{paciente.genero}</span>}
        {paciente.grupo_sanguineo && (
          <span className="text-red-700 font-medium">{paciente.grupo_sanguineo}</span>
        )}
        {paciente.seguro && <span className="text-slate-500">{paciente.seguro}</span>}
        <span className="text-slate-400 ml-auto">
          {estadisticas.consultas} {estadisticas.consultas === 1 ? 'consulta' : 'consultas'}
          {estadisticas.ultima && ` · última ${formatDate(estadisticas.ultima)}`}
        </span>
      </div>

      {alertas.length > 0 && (
        <div className="space-y-1.5">
          {alertas.map(({ tipo, titulo, contenido }) => (
            <div key={tipo}
                 className={`flex gap-2 p-2 rounded-md border text-sm ${
                   isCritical(tipo)
                     ? 'bg-red-50 border-red-200 text-red-900'
                     : 'bg-amber-50 border-amber-200 text-amber-900'
                 }`}>
              <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${isCritical(tipo) ? 'text-red-600' : 'text-amber-600'}`} />
              <div className="min-w-0">
                <span className="font-semibold">{titulo}: </span>
                <span>{contenido}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {consultas.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-2">
          Primera consulta de este paciente. No hay historial previo.
        </p>
      ) : (
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Stethoscope className="w-3.5 h-3.5" /> Consultas anteriores
          </p>
          <div className="space-y-1">
            {visibleConsultas.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onOpenConsultation?.(c)}
                disabled={!onOpenConsultation}
                className={`w-full text-left p-2 rounded-md border border-slate-200 text-sm ${
                  onOpenConsultation ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-slate-800 font-medium truncate">{c.diagnostico || c.motivo || 'Sin diagnóstico'}</span>
                  <span className="text-xs text-slate-400 shrink-0 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" /> {formatDate(c.fecha)}
                  </span>
                </div>
                {c.diagnostico && c.motivo && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">{c.motivo}</p>
                )}
                {c.medicamentos.length > 0 && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    <Pill className="w-3 h-3 inline mr-1" />
                    {c.medicamentos.join(', ')}
                  </p>
                )}
              </button>
            ))}
          </div>

          {consultas.length > VISIBLE && (
            <Button type="button" variant="ghost" size="sm" className="mt-1 w-full"
                    onClick={() => setShowAll(v => !v)}>
              {showAll
                ? <><ChevronUp className="w-4 h-4 mr-1" /> Ver menos</>
                : <><ChevronDown className="w-4 h-4 mr-1" /> Ver {hiddenCount} más</>}
            </Button>
          )}
        </div>
      )}

      {variant === 'full' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          {diagnosticos.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                Diagnósticos recurrentes
              </p>
              <div className="flex flex-wrap gap-1.5">
                {diagnosticos.map(({ diagnostico, veces }) => (
                  <Badge key={diagnostico} variant="secondary" className="font-normal">
                    {diagnostico}{veces > 1 && ` ×${veces}`}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {medicacion.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <Activity className="w-3.5 h-3.5" /> Medicación ya recetada
              </p>
              <div className="space-y-0.5">
                {medicacion.map(({ nombre, concentracion, fecha }) => (
                  <p key={nombre + fecha} className="text-sm text-slate-600">
                    {nombre} {concentracion && <span className="text-slate-400">{concentracion}</span>}
                    <span className="text-xs text-slate-400"> · {fecha}</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
